import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';

import { DisplayType } from '@models/module/fields/enums';
import {
  FontStyleType,
  HorizontalAlignType,
  LabelValueAlignmentType,
  ScreenFieldFormattingModel,
  VerticalAlignType,
} from '@models/module/fields/main';
import {
  DataCardSettingFontColorIndicatorDown,
  DataCardSettingFontColorIndicatorUp,
  DataCardTextSizeToPercent,
  DataCardViewPart,
} from '@models/module/main';
import { CipoStateComponent } from '@shared/components/cipo-state/state.component';
import { NumberProgressBarComponent } from '@shared/components/number-progress-bar/number-progress-bar.component';
import { PipesModule } from '@shared/modules/pipes.module';

@Component({
  selector: 'app-data-card-view-part',
  templateUrl: './data-card-view-part.component.html',
  styleUrls: ['./data-card-view-part.component.scss'],
  imports: [NumberProgressBarComponent, CipoStateComponent, CommonModule, FlexLayoutModule, PipesModule, MatIconModule],
})
export class DataCardViewPartComponent {
  @Input() part: DataCardViewPart;

  get showLabel(): boolean {
    return (
      [DisplayType.Text, DisplayType.CheckBox, DisplayType.Select].includes(
        this.part.field.displayTypeId || DisplayType.Text,
      ) && this.part.field.valueFormatting.size < 100
    );
  }

  get valueSize(): number {
    return this.showLabel ? this.part.field.valueFormatting.size : 100;
  }

  get isLabelValueSplit(): boolean {
    return (
      !this.part.field.valueFormatting.labelValueAlignment ||
      this.part.field.valueFormatting.labelValueAlignment == LabelValueAlignmentType.Split
    );
  }

  get isLabelValueJoin(): boolean {
    return this.part.field.valueFormatting.labelValueAlignment == LabelValueAlignmentType.Join;
  }

  get isLabelValueVertical(): boolean {
    return this.part.field.valueFormatting.labelValueAlignment == LabelValueAlignmentType.Vertical;
  }

  getFontWeight(partSetting: ScreenFieldFormattingModel): string {
    return this.hasFlag(partSetting.fontStyle, FontStyleType.Bold) ? 'bold' : 'normal';
  }

  getFontStyle(partSetting: ScreenFieldFormattingModel): string {
    return this.hasFlag(partSetting.fontStyle, FontStyleType.Italic) ? 'italic' : 'normal';
  }

  getNumberFontColor(partSetting: ScreenFieldFormattingModel): string {
    if (
      this.part.displayTypeId == DisplayType.IndicatorUp ||
      (this.part.displayTypeId == DisplayType.IndicatorBasedOnValue && this.part.valueNumber > 0) ||
      this.part.displayTypeId == DisplayType.PercentAmountProgress ||
      this.part.displayTypeId == DisplayType.PercentDurationProgress
    ) {
      return DataCardSettingFontColorIndicatorUp;
    }

    if (
      this.part.displayTypeId == DisplayType.IndicatorDown ||
      (this.part.displayTypeId == DisplayType.IndicatorBasedOnValue && this.part.valueNumber < 0)
    ) {
      return DataCardSettingFontColorIndicatorDown;
    }

    return partSetting.fontColor;
  }

  getFontColor(partSetting: ScreenFieldFormattingModel): string {
    if (this.part.isNumber) {
      return this.getNumberFontColor(partSetting);
    }
    return partSetting.fontColor;
  }

  getTextDecoration(partSetting: ScreenFieldFormattingModel): string {
    return this.hasFlag(partSetting.fontStyle, FontStyleType.Underlined) ? 'underline' : 'none';
  }

  getVerticalHorizontalAlign(partSetting: ScreenFieldFormattingModel): string {
    var horizontalAlign =
      partSetting.alignHorizontal == HorizontalAlignType.Left
        ? 'start'
        : partSetting.alignHorizontal == HorizontalAlignType.Right
          ? 'end'
          : 'center';
    var verticalAlign =
      partSetting.alignVertical == VerticalAlignType.Top
        ? 'start'
        : partSetting.alignVertical == VerticalAlignType.Bottom
          ? 'end'
          : 'center';
    if (this.isLabelValueVertical) {
      return `${verticalAlign} ${horizontalAlign}`;
    }
    return `${horizontalAlign} ${verticalAlign}`;
  }

  getFontSize(partSetting: ScreenFieldFormattingModel): string {
    return `${DataCardTextSizeToPercent.get(partSetting.fontSize)}%`;
  }

  get numberProgressValue(): number {
    return this.part.valueNumber > 100 ? 100 : this.part.valueNumber < 0 ? 0 : this.part.valueNumber;
  }

  private hasFlag(flags: number, flag: number): boolean {
    return (flags & flag) == flag;
  }
}
